import clsx from 'clsx'
import React, {
  InputHTMLAttributes,
  useCallback,
  useImperativeHandle,
  useRef,
  useState
} from 'react'

import { inputVariants } from '@/components-latest/primitives/Controls/Input'
import { Close, Search } from '@/components-latest/primitives/Icons'

import Styles from './SearchInput.module.scss'

interface ISearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onSubmit' | 'onChange'> {
  onClear: (value: string) => void
  onChange?: (value: string) => void
  onSubmit: (value: string) => void
  defaultValue?: string
}

const SearchInput = React.forwardRef<HTMLInputElement, ISearchInputProps>(
  (
    {
      className,
      children,
      onClear,
      onChange,
      onSubmit,
      defaultValue = '',
      ...props
    },
    ref
  ) => {
    const [value, setValue] = useState<string>(defaultValue)
    const innerRef = useRef<HTMLInputElement>(null)
    useImperativeHandle(ref, () => innerRef.current as HTMLInputElement)

    const focusInput = () => innerRef.current && innerRef.current.focus()
    const handleClear = () => {
      const cleanValue = ''
      setValue(cleanValue)
      onClear(cleanValue)
      onChange?.(cleanValue)
      focusInput()
    }
    const handleEnterPress = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Enter') return
        const updatedValue = e.currentTarget.value
        onSubmit(updatedValue)
      },
      [onSubmit]
    )
    const handleSubmit = useCallback(() => {
      onSubmit(value)
      focusInput()
    }, [onSubmit, value])
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const updatedValue = e.target.value
        setValue(updatedValue)
        onChange?.(updatedValue)
      },
      [onChange]
    )

    return (
      <div
        className={clsx(
          inputVariants({ intent: 'default' }),
          Styles.searchInput
        )}>
        <button
          onClick={handleSubmit}
          className={Styles.buttonIcons}
          data-testid="search-input-submit">
          <Search />
        </button>
        <input
          data-testid="search-input"
          className={clsx(Styles.input, className)}
          ref={innerRef}
          onKeyDown={handleEnterPress}
          onChange={handleChange}
          value={value}
          {...props}>
          {children}
        </input>
        <button
          onClick={handleClear}
          className={Styles.buttonIcons}
          data-testid="search-input-clear">
          {value && <Close className={Styles.closeIcon} />}
        </button>
      </div>
    )
  }
)
SearchInput.displayName = 'SearchInput'

export default SearchInput
